import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const AuthContext = createContext({
  session: null,
  user: null,
  signOut: async () => {},
  loading: true,
});

export function AuthProvider({ children }) {
  // SECURITY: Always start with null session and loading=true.
  // Never trust localStorage — always validate with server first.
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aal, setAal] = useState(null);
  const timerRef = useRef(null);

  const signOut = useCallback(async () => {
    // Clear session state immediately
    setSession(null);
    setAal(null);
    // Clear localStorage token
    try {
      const url = process.env.REACT_APP_SUPABASE_URL || '';
      const ref = url ? new URL(url).hostname.split('.')[0] : '';
      localStorage.removeItem(`sb-${ref}-auth-token`);
      // Also clear any app-level cached state
      localStorage.removeItem('finkloz_active_company_id');
      localStorage.removeItem('finkloz_security_settings');
    } catch { /* ignore */ }
    // Revoke token server-side
    supabase.auth.signOut().catch(() => {});
  }, []);

  // Inactivity auto-logout
  useEffect(() => {
    if (!session) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        signOut();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [session, signOut]);

  useEffect(() => {
    // Safety timeout — never stay stuck on loading screen
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    // SECURITY: Always validate session with the server.
    // This ensures expired/revoked tokens are caught.
    supabase.auth.getSession().then(async ({ data: { session: serverSession } }) => {
      if (serverSession) {
        // Verify the token is actually valid by checking with the server
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error || !user) {
          // Token is invalid or expired — force logout
          setSession(null);
          signOut();
        } else {
          setSession(serverSession);
          try {
            const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            setAal(data);
          } catch { /* MFA not available */ }
        }
      } else {
        setSession(null);
      }
      clearTimeout(timeout);
      setLoading(false);
    }).catch(() => {
      setSession(null);
      clearTimeout(timeout);
      setLoading(false);
    });

    // Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        if (_event === 'SIGNED_OUT') {
          setSession(null);
          setAal(null);
          return;
        }
        if (newSession) {
          setSession(newSession);
          try {
            const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            setAal(data);
          } catch { /* ignore */ }
        } else {
          setSession(null);
          setAal(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [signOut]);

  // Re-check auth state after MFA verify/enroll (no page reload needed)
  const refreshAuth = useCallback(async () => {
    // Force a session refresh to get the updated AAL token
    const { data: { session: refreshed } } = await supabase.auth.refreshSession();
    const s = refreshed || (await supabase.auth.getSession()).data.session;
    setSession(s);
    if (s) {
      try {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        setAal(data);
      } catch { /* ignore */ }
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      signOut,
      refreshAuth,
      loading,
      aal,
      needsMfaVerify: aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2',
      hasMfaEnrolled: aal?.nextLevel === 'aal2',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
